import React, { useState } from "react";

/**
 * Package Agreement modal — the shop-side opt-in gate (D32).
 *
 * Packages are OFF by default and cannot be offered until this agreement is accepted. Acceptance is
 * recorded server-side with its VERSION (`POST /api/packages/agreement/accept`), which is why this
 * component never sends a version of its own: the backend reads the current version from settings.
 * A client-supplied version would let a business record acceptance of an agreement it was never shown.
 *
 * The agreement text below is the human-readable rendering of
 * `docs/PACKAGES_SHOP_AGREEMENT_DRAFT.md`, keyed to AGREEMENT_VERSION. If the version in
 * PlatformSettings changes, this text must be updated to match, or a business would be shown one
 * agreement and recorded as having accepted another.
 */

export const AGREEMENT_VERSION = "packages-business-v1.0";

const sections = [
  {
    heading: "1. What this agreement covers",
    body: [
      "Packages are prepaid bundles of sessions sold to customers at a discount. This agreement governs your participation in them. Where it conflicts with the platform's general terms on packages, this agreement prevails.",
      "Packages are opt-in. You switch them on in settings, and you must accept this agreement first.",
    ],
  },
  {
    heading: "2. Your obligations",
    body: [
      "Every package you offer must have at least one subscribed professional. A package with no subscribed professional is not shown to customers and cannot be bought.",
      "You must deliver every session sold, including sessions in a package a customer has reactivated.",
      "You must not obstruct, filter or interfere with the platform's reminders to customers.",
      "You must not sell a package you cannot deliver.",
      "You must honour the customer terms shown at purchase. If you or your professional cancels, the session returns to the customer.",
      "You must keep your payment details, availability and professional list accurate.",
    ],
  },
  {
    heading: "3. How the money works",
    body: [
      "Everything the customer pays is held by the platform and paid out by the platform. Customer payments do not come to you directly.",
      "At purchase: the platform notifies you that the package has sold, and nothing is paid to you yet.",
      "At the first completed session: your portion is paid to you as one lump sum. The professionals' portion is retained by the platform.",
      "Per completed session: the platform releases one tranche to the professional who delivered it. The platform takes nothing.",
      "At final expiry: the platform retains the undelivered remainder in full. You receive no share of it.",
      "On reactivation: the reactivation fee goes entirely to the platform.",
      "The amount the platform retains at final expiry is earned for holding and administering the funds across the whole validity period, and applies to undelivered sessions only.",
    ],
  },
  {
    heading: "4. Clawback",
    body: [
      "We may recover from you the business portion of any undelivered, refunded package. This is normally effected as a negative reserve offset against your future payouts, not a demand for immediate payment.",
      "It applies when a refund is owed to a customer, including where nobody can deliver, or where you leave the platform.",
      "If your balance cannot cover it, the shortfall carries forward against future payouts.",
    ],
  },
  {
    heading: "5. Reactivation",
    body: [
      "Customers may reactivate an eligible package twice. You must deliver a reactivated package: reactivation restores the customer's whole remaining package at the price they paid, and you may not substitute a lesser service or refuse the sessions.",
      "Reactivation is the customer's right, not something you or the platform grants. You will not be asked to approve it and you may not block it.",
      "If you cannot deliver a reactivated package, a refund is owed.",
    ],
  },
  {
    heading: "6. Reminders",
    body: [
      "The platform runs the reminder programme, not the business. Reminders are the record that protects the forfeiture of unused sessions. Obstructing them undermines the whole model and is a breach of this agreement.",
    ],
  },
  {
    heading: "7. Ownership",
    body: [
      "A package has one owner at a time. Sharing or splitting a package between people is prohibited.",
      "A package may be gifted in whole to one new owner. All terms then follow the new owner.",
    ],
  },
  {
    heading: "8. Changes to this agreement",
    body: [
      "This agreement is subject to change from time to time, but never retrospectively.",
      "Changes are scheduled in advance with a stated effective date, and you are notified before a change takes effect. You must re-accept before creating new packages.",
      "A change never applies to a package already sold. Its economics — price, validity, the platform's share, the reactivation terms — are frozen on the date it was bought.",
      "If a material change is unacceptable, you may stop offering new packages. Packages already sold remain valid and must still be delivered.",
    ],
  },
  {
    heading: "9. Leaving the platform",
    body: [
      "If you leave OGESTYLE or move to another platform, you must repay the remaining unused package value to the customer at the full individual-service price, not the discounted price they paid. The customer must not bear any payment processing fee — you pay the exact remaining full amount.",
      "Example: 5 sessions sold for $150 ($30 each). 2 used. 3 remain. You repay $90. This is greater than an ordinary pro-rata refund, because it is a remedy for your leaving, and you accept it as such.",
    ],
  },
  {
    heading: "10. Liability",
    body: [
      "OGESTYLE is not a party to your agreement with your professionals. The platform's claim is always against you, never against your professionals. A professional who cannot be found or cannot pay does not reduce your obligation, and your own recovery against them is not a defence.",
    ],
  },
  {
    heading: "11. Suspension",
    body: [
      "We may suspend package selling for your business if packages are offered with no subscribed professional, if reminders are obstructed, or if clawback is repeatedly uncollectable. Suspension stops new sales; it does not cancel packages already sold, which must still be delivered or refunded.",
    ],
  },
];

const PackageAgreementModal = ({ onAccept, onCancel, busy = false, error = "" }) => {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        padding: "16px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          maxWidth: "720px",
          width: "100%",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb" }}>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "700" }}>
            📦 Package Participation Agreement
          </h2>
          <p style={{ margin: "6px 0 0", color: "#6b7280", fontSize: "13px" }}>
            Version {AGREEMENT_VERSION}. You must accept this before packages can be switched on.
          </p>
        </div>

        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
          {sections.map((section) => (
            <div key={section.heading} style={{ marginBottom: "18px" }}>
              <h3
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  margin: "0 0 6px",
                  color: "#111827",
                }}
              >
                {section.heading}
              </h3>
              {section.body.map((paragraph, index) => (
                <p
                  key={index}
                  style={{
                    margin: "0 0 6px",
                    fontSize: "13px",
                    lineHeight: "1.55",
                    color: "#374151",
                  }}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          ))}

          <p style={{ fontSize: "12px", color: "#9ca3af", marginTop: "16px" }}>
            This is a summary for review. The operative agreement is the version recorded against your
            business when you accept.
          </p>
        </div>

        {error ? (
          <div
            style={{
              margin: "0 24px",
              padding: "10px 12px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              color: "#991b1b",
              fontSize: "13px",
            }}
          >
            {error}
          </div>
        ) : null}

        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              cursor: "pointer",
              fontSize: "13px",
              color: "#374151",
            }}
          >
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              style={{ width: "16px", height: "16px", marginTop: "2px" }}
            />
            <span>
              I have read and accept the Package Participation Agreement, including the clawback of
              refunded packages, the duty to deliver reactivated packages, and the repayment obligation
              if my business leaves the platform.
            </span>
          </label>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                background: "white",
                color: "#374151",
                fontSize: "14px",
                fontWeight: "600",
                cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onAccept}
              disabled={!acknowledged || busy}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: "none",
                background: !acknowledged || busy ? "#9ca3af" : "#3b82f6",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: !acknowledged || busy ? "not-allowed" : "pointer",
              }}
            >
              {busy ? "Recording acceptance…" : "Accept and enable packages"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageAgreementModal;
